// src/auth/RequireAdmin.jsx
import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { auth, db } from "../firebase";
import { ref, get } from "firebase/database";

/**
 * RequireAdmin
 *
 * Protects routes that should be accessible only to admin users.
 *
 * Props:
 *   - children: React children to render when allowed
 *   - allowedRoles (optional): array of role strings, e.g. ["MAIN_ADMIN","ADMIN","HR"]
 *       Defaults to ["MAIN_ADMIN","ADMIN"] (use for admin-only pages).
 *
 * Notes:
 *  - This reads the admin record from /admins/{uid}.
 *  - It requires the record to be present, contain `role`, and `active !== false`.
 */
export default function RequireAdmin({ children, allowedRoles = ["MAIN_ADMIN", "ADMIN", "HR"] }) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!mounted) return;
      // no user => not allowed
      if (!u) {
        setAllowed(false);
        setChecking(false);
        return;
      }

      try {
        // read admin record from /admins/{uid}
        const adminSnap = await get(ref(db, `/admins/${u.uid}`));
        const adminRec = adminSnap.val() || null;

        if (!adminRec) {
          // no admin record
          setAllowed(false);
          return;
        }

        const role = String(adminRec.role || "").toUpperCase();
        const active = adminRec.active !== false; // treat missing as active

        if (!active) {
          setAllowed(false);
          return;
        }

        // compare against allowedRoles (normalize)
        const normAllowed = allowedRoles.map((r) => String(r || "").toUpperCase());
        setAllowed(normAllowed.includes(role));
      } catch (err) {
        console.error("RequireAdmin role check failed:", err);
        setAllowed(false);
      } finally {
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, [allowedRoles]);

  if (checking) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
